export const MENU_SCHOOL_YEAR_TYPES = [
  'PRIVATE_PK_12',
  'PUBLIC_PK_12',
  'PRIVATE_PRESCHOOL',
  'PUBLIC_PRESCHOOL',
  'PUBLIC_ADULTCARE',
] as const

export type MenuSchoolYearType = (typeof MENU_SCHOOL_YEAR_TYPES)[number]

export const MENU_SCHOOL_YEAR_TYPE_LABELS: Readonly<Record<MenuSchoolYearType, string>> = Object.freeze({
  PUBLIC_PK_12: 'Public School',
  PRIVATE_PK_12: 'Private School',
  PUBLIC_PRESCHOOL: 'Public Childcare',
  PRIVATE_PRESCHOOL: 'Private Childcare',
  PUBLIC_ADULTCARE: 'Public Adultcare',
})

export const MENU_SCHOOL_YEAR_AUDIENCES = ['Public', 'Private'] as const

export type MenuSchoolYearAudience = (typeof MENU_SCHOOL_YEAR_AUDIENCES)[number]

// Public/Private audience for each school-year type, keyed by the stable enum
// code rather than derived from the (mutable) display label. The Record is
// exhaustive: adding a MenuSchoolYearType without classifying it fails to compile.
export const MENU_SCHOOL_YEAR_TYPE_AUDIENCE: Readonly<Record<MenuSchoolYearType, MenuSchoolYearAudience>> =
  Object.freeze({
    PUBLIC_PK_12: 'Public',
    PRIVATE_PK_12: 'Private',
    PUBLIC_PRESCHOOL: 'Public',
    PRIVATE_PRESCHOOL: 'Private',
    PUBLIC_ADULTCARE: 'Public',
  })

export const MENU_SERVICE_SEASONS = ['SCHOOL_YEAR', 'SUMMER'] as const

export type MenuServiceSeason = (typeof MENU_SERVICE_SEASONS)[number]

export const MENU_SERVICE_SEASON_LABELS: Readonly<Record<MenuServiceSeason, string>> = Object.freeze({
  SCHOOL_YEAR: 'School Year Service',
  SUMMER: 'Summer Service',
})

export const MENU_PROGRAM_CODES = ['PRIVATE', 'NSLP', 'SBP', 'CACFP', 'FFVP', 'SMART_SNACKS', 'SFSP', 'SSO'] as const

export type MenuProgramCode = (typeof MENU_PROGRAM_CODES)[number]

export const MENU_PROGRAM_LABELS: Readonly<Record<MenuProgramCode, string>> = Object.freeze({
  PRIVATE: 'Private',
  NSLP: 'NSLP',
  SBP: 'SBP',
  CACFP: 'CACFP',
  FFVP: 'FFVP',
  SMART_SNACKS: 'Smart Snacks',
  SFSP: 'SFSP',
  SSO: 'SSO',
})

export const SCHOOL_YEAR_MENU_PROGRAM_CODES = ['PRIVATE', 'NSLP', 'SBP', 'CACFP', 'FFVP', 'SMART_SNACKS'] as const

export const SUMMER_MENU_PROGRAM_CODES = ['SFSP', 'SSO', 'CACFP', 'FFVP'] as const

export const MENU_PROGRAM_CODES_BY_SERVICE_SEASON: Readonly<Record<MenuServiceSeason, readonly MenuProgramCode[]>> =
  Object.freeze({
    SCHOOL_YEAR: SCHOOL_YEAR_MENU_PROGRAM_CODES,
    SUMMER: SUMMER_MENU_PROGRAM_CODES,
  })

export const MENU_SERVICE_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'] as const

export type MenuServiceDay = (typeof MENU_SERVICE_DAYS)[number]

/**
 * HALAL is an independent menu LINE (product decision 2026-07-19): a Halal
 * menu requires its own build due to the extent of menu changes, so it lives
 * alongside FRESH as its own line rather than as a toggleable substitution
 * scenario. It stays VISIBLE in scenario dietary filters as an indicator
 * (see HALAL_MENU_LINE_SCENARIO_HINT) but is not achievable via component
 * swaps.
 */
export const MENU_LINE_CODES = [
  'FRESH',
  'HALAL',
  'FAVES',
  'BAGGED_TO_GO',
  'A_LA_CARTE',
  'CUSTOM',
  'RESTRICTED',
  'BACK_UP',
  'HOLIDAY_TREAT',
  'HOLIDAY_MEAL',
  'HOLIDAY_A_LA_CARTE',
] as const

export type MenuLineCode = (typeof MENU_LINE_CODES)[number]

export const MENU_LINE_LABELS: Readonly<Record<MenuLineCode, string>> = Object.freeze({
  FRESH: 'Fresh',
  HALAL: 'Halal',
  FAVES: 'Faves',
  BAGGED_TO_GO: 'Field Trip Meals',
  A_LA_CARTE: 'A La Carte Sides',
  CUSTOM: 'Custom',
  RESTRICTED: 'Restricted Diet',
  BACK_UP: 'Fresh Backup Meals',
  // HOLIDAY_TREAT: free-treat templates (`MenuTemplate.holidayTreat`) — one
  // pinned Friday per month, a treat shipped with every meal. INTERNAL tier
  // (`HOLIDAY_TREAT_MENU_LINE_CODE` in menu-line-audience.ts).
  HOLIDAY_TREAT: 'Included Holiday Extras',
  HOLIDAY_MEAL: 'Holiday Meals',
  HOLIDAY_A_LA_CARTE: 'Holiday Treats',
})

/**
 * Doctor's-note restricted-diet menus. Authored centrally as restriction-PROFILE
 * templates ("Gluten-Free Lunch"), activated to a campus like any other menu,
 * then entitled to individual students through `SpecialDietMenuAssignment`.
 *
 * The line carries the AUDIENCE TIER only — it says "visible to entitled
 * students, not to the campus at large". It never says WHICH students: that is
 * per-menu entitlement data, resolved per request. See
 * `apps/ordo/server/src/managers/menu-audience.ts`.
 *
 * Deliberately named RESTRICTED, not SPECIAL_DIET: `Menu.settings.specialDiet`
 * already owns that phrase, and
 * `MenuLine.code` is `@unique` — expensive to rename later.
 */
export const RESTRICTED_MENU_LINE_CODE = 'RESTRICTED' satisfies MenuLineCode

/// Menu lines whose daily option buckets are user-configured per template
/// (option count + editable names, OPTION_n codes, isCustom buckets) instead
/// of prescribed from the bucket taxonomy. CUSTOM additionally uses a
/// free-text template name; A_LA_CARTE keeps the generated taxonomy name.
export const CONFIGURABLE_BUCKET_MENU_LINE_CODES: readonly MenuLineCode[] = Object.freeze([
  'A_LA_CARTE',
  'CUSTOM',
  'HOLIDAY_A_LA_CARTE',
])

export function menuLineUsesConfigurableBuckets(code: string | null | undefined): boolean {
  return code != null && (CONFIGURABLE_BUCKET_MENU_LINE_CODES as readonly string[]).includes(code)
}

/**
 * Template-identity variant codes. STANDARD must stay FIRST —
 * `MENU_VARIANTS[0]` is the fallback default across the menus app.
 * The regional codes (CALIFORNIA … ALASKA) serve public school-year
 * types; the COL codes serve private school-year types (see the
 * school-type-aware choice list in the menus app). NOT a pricing axis:
 * each variant is its own MenuTemplate carrying its own single price.
 *
 * BAY_AREA was retired 2026-07-19 and removed outright — no prod rows
 * carried it (product-owner confirmed), so there is no alias/migration;
 * label helpers' formatToken-style fallbacks cover any theoretical stray.
 */
export const PUBLIC_MENU_VARIANT_CODES = [
  'STANDARD',
  'CALIFORNIA',
  'PNW',
  'THE_ROCKIES',
  'SOUTHWEST',
  'SOUTHEAST',
  'MIDWEST',
  'NORTHEAST',
  'HAWAII',
  'ALASKA',
] as const

export const PRIVATE_MENU_VARIANT_CODES = ['STANDARD_COL', 'PREMIUM_COL', 'MAX_COL'] as const

export const MENU_VARIANTS = [...PUBLIC_MENU_VARIANT_CODES, ...PRIVATE_MENU_VARIANT_CODES] as const

export type MenuVariant = (typeof MENU_VARIANTS)[number]

export const MENU_VARIANT_LABELS: Readonly<Record<MenuVariant, string>> = Object.freeze({
  STANDARD: 'Standard',
  CALIFORNIA: 'California',
  PNW: 'PNW',
  THE_ROCKIES: 'The Rockies',
  SOUTHWEST: 'Southwest',
  SOUTHEAST: 'Southeast',
  MIDWEST: 'Midwest',
  NORTHEAST: 'Northeast',
  HAWAII: 'Hawaii',
  ALASKA: 'Alaska',
  // Private-school cost-of-living tiers keep their stored _COL codes but
  // read as plain Standard/Premium/Max (product decision 2026-07-19).
  STANDARD_COL: 'Standard',
  PREMIUM_COL: 'Premium',
  MAX_COL: 'Max',
})

/**
 * The menu line a template GROUPS/RENDERS under. Halal menus are their own
 * menu line (alongside FRESH), but pre-existing templates were authored as a
 * FRESH-line template carrying `menuType: 'HALAL'`. Wherever menus/templates
 * group or label by menu line, resolve the line through this helper so
 * Halal-tagged templates surface under the Halal line without a data
 * migration. Falls back to FRESH when no line is stored.
 */
export function effectiveMenuLineCode(
  menuLineCode: string | null | undefined,
  menuType: string | null | undefined,
): string {
  if (menuType === 'HALAL') {
    return 'HALAL'
  }
  return menuLineCode != null && menuLineCode !== '' ? menuLineCode : MENU_LINE_CODES[0]
}

/**
 * Scenario dietary filters: every provisioned dietary need is a toggleable
 * substitution scenario. HALAL was special-cased as a display-only indicator
 * while it was served as its own menu line (product decision 2026-07-19);
 * the Dietary Support rules engine (per-need date windows + certified-tag
 * predicate + replacements) reversed that on 2026-07-20 — Halal now behaves
 * like PORK_FREE/BEEF_FREE, and the Halal menu line is retired (relabeled +
 * archived in data; the seeders no longer resurrect it).
 */
export function menuDietaryNeedIsToggleableScenario(_code: string | null | undefined): boolean {
  return true
}

/**
 * @deprecated The Halal menu line is retired — Halal is a normal toggleable
 * dietary scenario now. Kept only so stale imports fail loudly in review
 * rather than at runtime; do not render this hint.
 */
export const HALAL_MENU_LINE_SCENARIO_HINT = 'Halal is served as its own menu line'

/**
 * Fresh vs Halal template IDENTITY (`MenuTemplate.menuType`). A second
 * field beside `variant` — never composed into the variant string, and
 * NOT a pricing axis: a Halal template is simply its own template with
 * its own authored price. Distinct from the `HALAL` DIETARY NEED below
 * (component swaps within a template, no price change) and from the
 * `HALAL` menu-line code (the line Halal templates group/render under —
 * see `effectiveMenuLineCode`) — three separate concepts.
 */
export const MENU_TYPES = ['FRESH', 'HALAL'] as const

export type MenuTypeCode = (typeof MENU_TYPES)[number]

export const MENU_TYPE_LABELS: Readonly<Record<MenuTypeCode, string>> = Object.freeze({
  FRESH: 'Fresh',
  HALAL: 'Halal',
})

export const MENU_DIETARY_NEEDS = ['HALAL', 'KOSHER', 'PORK_FREE', 'BEEF_FREE', 'OBSERVES_LENT'] as const

export type MenuDietaryNeed = (typeof MENU_DIETARY_NEEDS)[number]

export const MENU_DIETARY_NEED_LABELS: Readonly<Record<MenuDietaryNeed, string>> = Object.freeze({
  HALAL: 'Halal',
  KOSHER: 'Kosher',
  PORK_FREE: 'Pork Free',
  BEEF_FREE: 'Beef Free',
  OBSERVES_LENT: 'Observes Lent',
})

/// Dietary Support FLAGS — bucket-targeted template rules (the menus app's
/// Dietary Support tab) that are NOT customer dietary needs: they never join
/// MENU_DIETARY_NEEDS, so school/family dietary pickers and dietary-need
/// provisioning ignore them. Offered only on A_LA_CARTE-line templates.
export const MENU_DIETARY_SUPPORT_FLAGS = ['NO_SUGARY_FOODS', 'NO_BEVERAGES'] as const

export type MenuDietarySupportFlag = (typeof MENU_DIETARY_SUPPORT_FLAGS)[number]

/// Every code the Dietary Support rules machinery accepts: the customer
/// dietary needs plus the bucket-targeted flags.
export const MENU_DIETARY_SUPPORT_NEEDS = [...MENU_DIETARY_NEEDS, ...MENU_DIETARY_SUPPORT_FLAGS] as const

export type MenuDietarySupportNeed = (typeof MENU_DIETARY_SUPPORT_NEEDS)[number]

export const MENU_DIETARY_SUPPORT_NEED_LABELS: Readonly<Record<MenuDietarySupportNeed, string>> = Object.freeze({
  ...MENU_DIETARY_NEED_LABELS,
  NO_SUGARY_FOODS: 'No Sugary Foods',
  NO_BEVERAGES: 'No Beverages',
})

export const MENU_SPECIAL_SERVICE_KINDS = ['HOT_SERVICE', 'COLD_SERVICE', 'BAGGED_LUNCH', 'PIZZA_DAY'] as const

export type MenuSpecialServiceKind = (typeof MENU_SPECIAL_SERVICE_KINDS)[number]

export const MENU_SPECIAL_SERVICE_KIND_LABELS: Readonly<Record<MenuSpecialServiceKind, string>> = Object.freeze({
  HOT_SERVICE: 'Hot Service',
  COLD_SERVICE: 'Cold Service',
  BAGGED_LUNCH: 'Field Trip Meals',
  PIZZA_DAY: 'Pizza Day',
})

export const MENU_BUCKET_CODES = [
  'MAIN',
  'VEG',
  'COLD_SANDO_SALAD',
  'GF_DF',
  'DF_EF',
  'BEST_SELLER',
  'CEREAL',
  'SIDE',
  'BEVERAGE',
] as const

export type MenuBucketCode = (typeof MENU_BUCKET_CODES)[number]

export interface MenuBucketDefinition {
  code: MenuBucketCode
  name: string
  tagSlug: string
  /**
   * Solid hex seeded onto the bucket's MENU_SLOT tag. MUST equal
   * `MENU_BADGE_COLOR_HEX[MENU_BUCKET_COLOR_KEYS[code]]` (menu-line-style.ts)
   * — the badge key table is the source of truth (PROD-1304); this hex is
   * its projection for surfaces that store a color value. Pinned by test.
   */
  color: string
  sortOrder: number
}

export const MENU_BUCKET_DEFINITIONS: readonly MenuBucketDefinition[] = Object.freeze([
  { code: 'MAIN', name: 'Daily Special', tagSlug: 'menu-slot-meat', color: '#9333ea', sortOrder: 10 },
  { code: 'VEG', name: 'Veg Special', tagSlug: 'menu-slot-vegetarian', color: '#16a34a', sortOrder: 20 },
  {
    code: 'COLD_SANDO_SALAD',
    name: 'Cold Salad/Sando',
    tagSlug: 'menu-slot-sub-wrap',
    color: '#0891b2',
    sortOrder: 30,
  },
  { code: 'GF_DF', name: 'GF/DF Option', tagSlug: 'menu-slot-allergen-free', color: '#2563eb', sortOrder: 40 },
  /// Preschool (childcare) menus swap GF/DF for a Dairy-Free/Egg-Free option.
  { code: 'DF_EF', name: 'DF/EF Option', tagSlug: 'menu-slot-dairy-egg-free', color: '#db2777', sortOrder: 45 },
  { code: 'BEST_SELLER', name: 'Best Seller', tagSlug: 'menu-slot-best-seller', color: '#d97706', sortOrder: 50 },
  { code: 'CEREAL', name: 'Cereal', tagSlug: 'menu-slot-cereal', color: '#ea580c', sortOrder: 60 },
  /// Standalone a-la-carte menus (e.g. Private School Sides/Beverages).
  { code: 'SIDE', name: 'A La Carte Side', tagSlug: 'menu-slot-a-la-carte-side', color: '#dc2626', sortOrder: 70 },
  { code: 'BEVERAGE', name: 'Beverage', tagSlug: 'menu-slot-beverage', color: '#475569', sortOrder: 80 },
])

export const MENU_BUCKET_TAG_SLUG_BY_CODE: Readonly<Record<MenuBucketCode, string>> = Object.freeze(
  MENU_BUCKET_DEFINITIONS.reduce(
    (acc, bucket) => {
      acc[bucket.code] = bucket.tagSlug
      return acc
    },
    {} as Record<MenuBucketCode, string>,
  ),
)

export function menuBucketDefinitionForCode(code: string): MenuBucketDefinition | null {
  return MENU_BUCKET_DEFINITIONS.find((bucket) => bucket.code === code) ?? null
}

export function menuProgramCodesForServiceSeason(season: string): readonly MenuProgramCode[] {
  return MENU_PROGRAM_CODES_BY_SERVICE_SEASON[season as MenuServiceSeason] ?? SCHOOL_YEAR_MENU_PROGRAM_CODES
}
